/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { ApplicationInsights, ICustomProperties } from "@microsoft/applicationinsights-web";
import { ReactPlugin, useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { runtimeEnv } from "configuration";

const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    //disableTelemetry: process.env.NODE_ENV === "development", // Comment out if you need AppInsights events recorded on local dev machine
    disableTelemetry: false,
    instrumentationKey: runtimeEnv.REACT_APP_AI_KEY,
    maxBatchInterval: 15,
    disableFetchTracking: false,
    // For react-router v6 or other scenarios where router history isn't exposed, Application Insights configuration enableAutoRouteTracking can be used to auto-track router changes
    // https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-react-plugin
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    disableExceptionTracking: true, // Need to track exceptions manually, otherwise it reports all user errors within his sandbox files
    correlationHeaderDomains: ["*connect-imodeldeveloperservices.bentley.com"],
    extensions: [reactPlugin],
    excludeRequestFromAutoTrackingPatterns: [/thumbnail$/, /.*\.json$/],
  },
});

appInsights.loadAppInsights();

export { reactPlugin, appInsights };

export type TrackedSandboxEventType = "Sandbox published" | "Sandbox forked" | "Sandbox migrated" | "Sandbox view" | "Featured sandbox selection click" | "Sandbox liked" | "Search";

export interface TrackedSandboxEvent extends ICustomProperties {
  SandboxUser: string,
  SandboxUserId: string,
  SandboxName: string,
  SandboxId: string,
}

export const useTrackSandboxEvent = function <T extends ICustomProperties>(eventType: TrackedSandboxEventType) {
  const appInsights = useAppInsightsContext();
  return (data?: T) => {
    appInsights.trackEvent({ name: eventType }, data);
  };
}

export const trackComponent = function <T extends ICustomProperties>(appInsights: ReactPlugin, componentName: string, duration: number, location: string, data?: T) {

  const metricData = {
    average: Math.floor(duration / 1000),
    name: "React Component Engaged Time (seconds)",
    sampleCount: 1,
  };
  const properties = { "Component Name": componentName, ...(data ?? {}) };

  // trackComponent is typically called on component destruction, but at that moment router already changes location to different one
  // appinsighs context grabs the current location (incorrect), which makes it harder to correlated events later
  // settings manually location to fix it
  appInsights.core.addTelemetryInitializer(envelope => {
    if (envelope && envelope.tags)
      envelope.tags["ai.operation.name"] = runtimeEnv.REACT_APP_BASEURL + location;
  });

  appInsights.trackMetric(metricData, properties);
}